import { LicensingAgencyStatus } from '@/@types/generated/index.ts'
import { buildPath, Params } from '@/routes.tsx'
import { useEffect } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router'
import { useLicensingAgency } from '../hooks/licensing-agency-provider.tsx'
import { useLicensingAgencyStepper } from './LicensingAgencySteps.tsx'

// Explicitly narrow the eligible route keys for this guard
// NOTE: If any of these paths are invalid, buildPath fails below
//       so no type satisfaction check is needed here
type LicensingAgencyRouteKey =
  | '/applications/licensing-agency/:agencyId/success'
  | '/licensing-agencies/:agencyId'
  | '/applications/licensing-agency/:agencyId'
  | '/applications/licensing-agency/:agencyId/business-info'
  | '/applications/licensing-agency/:agencyId/documentation'
  | '/applications/licensing-agency/:agencyId/certify'

interface StatusGuard {
  redirectTo: LicensingAgencyRouteKey
  // Paths the user may remain on. Defaults to [redirectTo]. Terminal steps are
  // deliberately excluded so a rejected applicant can edit and resubmit, but
  // cannot land back on the "Application Submitted" page.
  allow?: readonly LicensingAgencyRouteKey[]
}

const guards = {
  [LicensingAgencyStatus.DRAFT]: null,
  [LicensingAgencyStatus.UNDER_REVIEW]: {
    redirectTo: '/applications/licensing-agency/:agencyId/success',
  },
  [LicensingAgencyStatus.ACTIVE]: {
    redirectTo: '/licensing-agencies/:agencyId',
  },
  [LicensingAgencyStatus.SUSPENDED]: {
    redirectTo: '/licensing-agencies/:agencyId',
  },
  [LicensingAgencyStatus.REJECTED]: {
    redirectTo: '/applications/licensing-agency/:agencyId',
    allow: [
      '/applications/licensing-agency/:agencyId',
      '/applications/licensing-agency/:agencyId/business-info',
      '/applications/licensing-agency/:agencyId/documentation',
      '/applications/licensing-agency/:agencyId/certify',
    ],
  },
  [LicensingAgencyStatus.REVOKED]: {
    redirectTo: '/licensing-agencies/:agencyId',
  },
} as const satisfies Record<LicensingAgencyStatus, StatusGuard | null>

// Guard to ensure users are not presented with invalid steps (ie, if their licensing agency is already active)
export function LicensingAgencyStepGuard() {
  // Hooks
  const { loading, licensingAgency } = useLicensingAgency()
  const { activeStep } = useLicensingAgencyStepper()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { agencyId } =
    useParams<Params<'/applications/licensing-agency/:agencyId'>>()

  // Redirect to success page if registration is complete
  useEffect(() => {
    // Loading, do nothing
    if (loading || !agencyId || !licensingAgency) return

    const guard: StatusGuard | null = guards[licensingAgency.status]
    if (!guard) return

    const allowed = (guard.allow ?? [guard.redirectTo]).map((r) =>
      buildPath(r, { agencyId })
    )
    if (allowed.includes(pathname)) return

    navigate(buildPath(guard.redirectTo, { agencyId }), { replace: true })
  }, [activeStep, licensingAgency, loading, pathname, agencyId, navigate])

  return <Outlet />
}
